import type { Segment } from '../util/intervals';
import type { SearchResultHit, LoadedItem, ClientItem } from '../dto/searchResultHit';
import { isItem } from '../dto/searchResultHit';
import type { Celebrity, Label, Person, SearchCategoryCount } from '../dto/searchModels';


export const SEARCH_FIELD_PREFIX = 'sf_';

export interface HistogramBucket {
  key: number;
  key_as_string: string; // Date string
  doc_count: number;
}

export type HistogramResult = Record<string, HistogramBucket>;

/**
 * TODO: Fix DTO type definition duplication with Search interface from
 * server/core/src/model/search.ts
 */
export interface SearchResult {
  count: number;
  typeCount: SearchCategoryCount[];
  totalAcrossPages?: number;
  total: number;
  from: number;
  indexOfCurrentLoadedItemInHits: number;
  hits: SearchResultHit[];
  dateHits: HistogramResult;
  incompleteFolderCount: null | number;
  truncated: boolean;
}

export type SearchResultViewModel = SearchResult & {
  isRecycleBin: boolean;
};


// All state field names - typed so it becomes more concise to use in code
export type StateFieldName = 'transcodingState' | 'transcriptionState' | 'personDetectionState' | 'celebrityDetectionState' |
                             'labelDetectionState' | 'textDetectionState' | 'archiveFileTransferState';

export type EnabledAnalyticsFieldName = 'transcriptionEnabled' | 'personDetectionEnabled' | 'celebrityDetectionEnabled' |
  'labelDetectionEnabled' | 'textDetectionEnabled';

export type ErrorMessageFieldName = 'transcodingErrorMessage' | 'transcriptionErrorMessage' | 'personDetectionErrorMessage' | 'celebrityDetectionErrorMessage' |
  'labelDetectionErrorMessage' | 'textDetectionErrorMessage' | 'archiveFileTransferErrorMessage';


export interface EntityViewModel {
  isSelected: boolean;
}

export interface SequencePartsTrackObject {
  segments?: Segment[];
}

export type Human = Celebrity | Person;
export type Entity = Label | Human | SequencePartsTrackObject;
export type ExtendedEntity = Entity & EntityViewModel;
export type HumanVM = Human & EntityViewModel;
export type LabelVM = Label & EntityViewModel & { color: string };

export const isLoadedItem = (item: unknown): item is LoadedItem => {
  return isItem(item) && 'loaded' in item && item.loaded as boolean;
};

export const isInstanceOfPerson = (person: Person | Celebrity | ExtendedEntity | HumanVM): person is Person => {
  const probablePerson = person as Partial<Person>;
  const validPersonId = typeof probablePerson.personId === 'string';
  const validName = typeof probablePerson.name === 'string' || probablePerson.name === undefined;

  return validPersonId && validName;
};

export const isInstanceOfCelebrity = (celebrity: Person | Celebrity | ExtendedEntity | HumanVM): celebrity is Celebrity => {
  const probableCelebrity = celebrity as Partial<Celebrity>;
  const validCelebrityId = typeof probableCelebrity.celebrityId === 'string';
  const validName = typeof probableCelebrity.name === 'string';

  return validCelebrityId && validName;
};

export const SYSTEM_FIELD_ID_QUERYPARAM = 'mj_sfields';
export const TECHNICAL_FIELD_ID_QUERYPARAM = 'technicalMetadata';

export const MEDIA_DURATION_SORT_PROPERTY_ID = 'mediaDuration' satisfies keyof ClientItem;
